import { createSelector, createSlice } from "@reduxjs/toolkit";
import { logout } from "../common/reducers/initContext";
import { dataHookFactory, initMapFactory, DefaultFailureAction, DefaultSuccessAction } from "../common/reducers/utilities";
import { ensureArray, groupBy } from "../common/utils/arrayUtilities";
import { markFetched, registerEntityBasedFetch } from "../common/hooks/initHook";

const key = n => n.id;

const ticketNotesSlice = createSlice({
    name: 'ticketNotes',
    initialState: {
        data: {}
    },
    reducers: {
        set: (state, action) => {
            ensureArray(action.payload.ids).forEach(id => delete state.data[id]);
            const byTicket = groupBy(ensureArray(action.payload.data), 'ticketid');
            Object.entries(byTicket).forEach(([ ticketid, notes ]) => state.data[ticketid] = notes);
        },
        addOrUpdate: (state, action) => {
            const notes = ensureArray(action.payload);
            const byTicket = groupBy(notes, 'ticketid');
            Object.entries(byTicket).forEach(([ ticketid, notes ]) => {
                const ids = new Set(notes.map(n => key(n)));
                state.data[ticketid] = (state.data[ticketid]?.filter(n => !ids.has(key(n))) ?? []).concat(notes);
            })
        },
        remove: (state, action) => {
            const toRemove = ensureArray(action.payload);
            const byTicket = groupBy(toRemove, 'ticketid');
            Object.entries(byTicket).forEach(([ ticketid, notes ]) => {
                const ids = new Set(notes.map(n => key(n)));
                state.data[ticketid] = state.data[ticketid].filter(n => !ids.has(key(n)));
            });
        }
    },
    extraReducers: builder => {
        builder.addCase(logout, () => ticketNotesSlice.getInitialState());
    }
});


// #region network operations
const visualizeSuccess = (verb, action) => DefaultSuccessAction(verb, 'ticketNote', action);
const visualizeFailure = verb => DefaultFailureAction(verb, 'ticketNote');


const fetchTicketNotes = ticketid => network => network.get('/api/v1/ticketnotes/' + encodeURIComponent(ticketid), data => ticketNotesSlice.actions.set({ data, ids: [ticketid] }), visualizeFailure('get'));
export const addTicketNote = (network, data) => network.post('/api/v1/ticketnote', data, ticketNotesSlice.actions.addOrUpdate, visualizeFailure('create'));
// #endregion

// #region selectors
const selectAllTicketNoteData = state => state.ticketNotes.data;
const selectTicketNotes = ticketid => createSelector([selectAllTicketNoteData], notes => notes[ticketid] ?? []);
// #endregion

const listen = {
    ticketNoteCreated: (note, { dispatch, websocket }) => {
        markFetched(websocket, 'ticket_note', key(note))
        dispatch(ticketNotesSlice.actions.addOrUpdate(note))
    }
}

const ticketInitNotesByID = initMapFactory(fetchTicketNotes, listen);
registerEntityBasedFetch('ticket', ticketInitNotesByID);

const cleanup = { root: ticketNotesSlice.name, all: state => Object.values(state.ticketNotes).flat(), removeAll: ticketNotesSlice.actions.remove, key };
export const [useTicketNotes] = dataHookFactory(selectTicketNotes, ticketInitNotesByID, {cleanup});

const reducer = { name: ticketNotesSlice.name, reducer: ticketNotesSlice.reducer }
export default reducer;